// PartnerTab.js - Enhanced with Gmail OAuth linking (FIXED with Redirect Flow)
/**
 * GMAIL OAUTH REDIRECT URI CONFIGURATION
 * ========================================
 *
 * This component uses Google OAuth 2.0 redirect flow for Gmail integration.
 *
 * CRITICAL CONFIGURATION REQUIREMENTS:
 *
 * 1. REDIRECT URI (Line 36):
 *    - Current: window.location.origin + "/creatorconnect/redirect"
 *    - Production: https://www.useblitz.co/creatorconnect/redirect
 *    - This MUST match EXACTLY what's configured in Google Cloud Console
 *
 * 2. GOOGLE CLOUD CONSOLE SETUP:
 *    - Go to: https://console.cloud.google.com/apis/credentials
 *    - Select your OAuth 2.0 Client ID (Web application)
 *    - Under "Authorized redirect URIs", add:
 *      https://www.useblitz.co/creatorconnect/redirect
 *    - Save changes
 *
 * 3. ENVIRONMENT VARIABLE (required):
 *    - Vercel: REACT_APP_GOOGLE_CLIENT_ID must be set in Vercel dashboard
 *    - Local: Add to .env.local file
 *    - Verify: Check console logs on page load (lines 38-43)
 *
 * 4. ROUTE CONFIGURATION:
 *    - Route: /creatorconnect/redirect (routes.js line 66)
 *    - Component: CreatorRedirect (App.js lines 1641-1649)
 *    - The route MUST be accessible (not behind authentication)
 *
 * OAUTH FLOW:
 * 1. User clicks "Connect Gmail Account" → handleGmailLinkClick()
 * 2. Backend creates GoogleRequest, returns request_id
 * 3. googleLogin({ state: request_id }) triggers redirect to Google
 * 4. User authorizes on Google
 * 5. Google redirects to: https://www.useblitz.co/creatorconnect/redirect?code=XXX&state=request_id
 * 6. CreatorRedirect component processes code and state
 * 7. Backend exchanges code for tokens, stores refresh_token
 * 8. User redirected back to settings with success message
 *
 * TROUBLESHOOTING:
 * - If redirect fails, check browser console for debug logs
 * - Verify Google Cloud Console redirect URI matches exactly
 * - Check that REACT_APP_GOOGLE_CLIENT_ID is set
 * - Ensure /creatorconnect/redirect route is publicly accessible
 */
// PartnerTab.js - Complete Rewrite with Status API Integration
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  TextField,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import { buildGoogleAuthUrl } from "../../../Utils/googleAuthUrl";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import EmailIcon from "@mui/icons-material/Email";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SpeedIcon from "@mui/icons-material/Speed";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import InfoIcon from "@mui/icons-material/Info";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import client from "../../../../API";

const GMAIL_SCOPES = [
  "https://www.googleapis.com/auth/gmail.modify",
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile"
].join(" ");

const REDIRECT_URI = window.location.origin + "/creatorconnect/redirect";

// ==================== SUB-COMPONENTS ====================

const PartnerBenefits = () => (
  <Card sx={{ mb: 3, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
    <CardContent>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <UpgradeIcon /> Partner Status Benefits
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", alignItems: "start", gap: 1, mb: 2 }}>
            <AutoFixHighIcon sx={{ mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>AI Email Agent</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Automatically negotiate deals and respond to brands 24/7
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", alignItems: "start", gap: 1, mb: 2 }}>
            <SpeedIcon sx={{ mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Instant Responses</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Never miss an opportunity with automated, intelligent replies
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", alignItems: "start", gap: 1, mb: 2 }}>
            <AttachMoneyIcon sx={{ mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Higher Rates</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                AI negotiates competitive rates based on market data
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", alignItems: "start", gap: 1, mb: 2 }}>
            <TrendingUpIcon sx={{ mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Deal Tracking</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Automatic deal organization and progress tracking
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

const StatusCard = ({ status, isGmailConnected, emailProcessing, onRefresh, refreshing }) => {
  const isPartner = status === "Partner";
  
  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        mb: 3,
        border: 2,
        borderColor: isPartner ? "success.main" : "warning.main",
        background: isPartner 
          ? "linear-gradient(135deg, #f5f7fa 0%, #e8f5e9 100%)"
          : "linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isPartner ? (
            <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
          ) : (
            <WarningIcon sx={{ fontSize: 40, color: "warning.main" }} />
          )}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: isPartner ? "success.dark" : "warning.dark" }}>
              {isPartner ? "Partner Status Active" : "Associate Status"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isPartner 
                ? "You have full AI email automation enabled"
                : "Connect Gmail to unlock Partner benefits"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Tooltip title="Refresh Status">
            <IconButton 
              onClick={onRefresh} 
              disabled={refreshing}
              size="small"
              sx={{ 
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Chip 
            label={status} 
            color={isPartner ? "success" : "warning"}
            sx={{ fontWeight: 600, fontSize: "0.9rem", px: 2 }}
          />
        </Box>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: "center" }}>
            <EmailIcon sx={{ fontSize: 32, color: isGmailConnected ? "success.main" : "text.secondary", mb: 1 }} />
            <Typography variant="subtitle2" gutterBottom>Gmail Connection</Typography>
            <Chip 
              label={isGmailConnected ? "Connected" : "Not Connected"}
              size="small"
              color={isGmailConnected ? "success" : "default"}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: "center" }}>
            <AutoFixHighIcon sx={{ fontSize: 32, color: emailProcessing ? "success.main" : "text.secondary", mb: 1 }} />
            <Typography variant="subtitle2" gutterBottom>AI Agent</Typography>
            <Chip 
              label={emailProcessing ? "Active" : "Inactive"}
              size="small"
              color={emailProcessing ? "success" : "default"}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: "center" }}>
            <SecurityIcon sx={{ fontSize: 32, color: isPartner ? "success.main" : "text.secondary", mb: 1 }} />
            <Typography variant="subtitle2" gutterBottom>Status Level</Typography>
            <Chip 
              label={isPartner ? "Partner" : "Associate"}
              size="small"
              color={isPartner ? "success" : "warning"}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

const ConsentDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
    <DialogTitle>Gmail Access Consent</DialogTitle>
    <DialogContent>
      <Typography paragraph>
        To enable Partner features, we need access to your Gmail account to:
      </Typography>
      <List>
        <ListItem>
          <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
          <ListItemText primary="Read partnership emails" />
        </ListItem>
        <ListItem>
          <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
          <ListItemText primary="Send automated responses" />
        </ListItem>
        <ListItem>
          <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
          <ListItemText primary="Organize partnership communications" />
        </ListItem>
      </List>
      <Alert severity="info" sx={{ mt: 2 }}>
        You can revoke this access at any time from your Google account settings.
      </Alert>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => onClose(false)}>Cancel</Button>
      <Button variant="contained" onClick={() => onClose(true)}>
        I Agree - Connect Gmail
      </Button>
    </DialogActions>
  </Dialog>
);

const ForwardedAliasEditor = ({ currentAlias, onSave, onCancel }) => {
  const [alias, setAlias] = useState(currentAlias || "");
  const [error, setError] = useState("");

  const handleSave = () => {
    if (alias && !alias.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }
    onSave(alias);
  };

  return (
    <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Email Forwarding Address
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Change your Partnership Email
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          value={alias}
          onChange={(e) => {
            setAlias(e.target.value);
            setError("");
          }}
          placeholder="forwarding@example.com"
          error={!!error}
          helperText={error}
        />
        <Button 
          variant="contained" 
          size="small" 
          onClick={handleSave}
          startIcon={<SaveIcon />}
        >
          Save
        </Button>
        <Button 
          variant="outlined" 
          size="small" 
          onClick={onCancel}
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

// ==================== MAIN COMPONENT ====================

export default function PartnerTab({ tabValue, index }) {
  // ========== STATE MANAGEMENT ==========
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  
  // Status from API
  const [status, setStatus] = useState({
    creator_status: "Associate",
    has_gmail_connected: false,
    process_emails: false,
    forwarded_alias: null,
    email: null
  });
  
  // UI State
  const [consentOpen, setConsentOpen] = useState(false);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [editingAlias, setEditingAlias] = useState(false);

  const isGmailConnected = status.has_gmail_connected;
  const isPartner = status.creator_status === "Partner";

  // ========== FETCH STATUS FROM API ==========
  const fetchStatus = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setStatusLoading(true);
      
      const result = await client.creatorConnect.getStatus();
      
      if (result.success) {
        setStatus({
          creator_status: result.status || "Associate",
          has_gmail_connected: result.has_gmail_connected || false,
          process_emails: result.process_emails || false,
          forwarded_alias: result.forwarded_alias || null,
          email: result.email || null
        });
        console.log("✅ Status refreshed:", result);
      } else {
        throw new Error(result.error || "Failed to fetch status");
      }
    } catch (err) {
      console.error("❌ Status fetch error:", err);
      setErrorMsg(`Failed to load status: ${err.message}`);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  // ========== INITIAL LOAD ==========
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // ========== HANDLE OAUTH CALLBACK ==========
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const upgradeSuccess = urlParams.get('upgrade_success');
    const gmailConnected = urlParams.get('gmailConnected');
    const error = urlParams.get('error');

    if (upgradeSuccess === 'true' || gmailConnected === 'true') {
      setSuccessMsg("✅ Gmail connected successfully! You are now a Partner with full AI automation.");
      
      // Clean URL
      urlParams.delete('upgrade_success');
      urlParams.delete('gmailConnected');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);

      // Fetch fresh status
      setTimeout(() => fetchStatus(false), 500);
    }

    if (error) {
      setErrorMsg(`Connection failed: ${decodeURIComponent(error)}`);
      urlParams.delete('error');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, [fetchStatus]);

  // ==================== HANDLERS ====================

  const handleUpgradeToPartner = () => {
    setErrorMsg("");
    setSuccessMsg("");
    setConsentOpen(true);
  };

  const handleConsentClose = async (agreed) => {
    setConsentOpen(false);
    if (!agreed) return;

    setLoading(true);
    setErrorMsg("");

    try {
      console.log("=== CREATING PARTNER UPGRADE REQUEST ===");
      
      const result = await client.creatorConnect.upgradeToPartner();

      if (!result.success || result.error) {
        console.error("❌ Server error:", result);
        throw new Error(result.error || 'Failed to create upgrade request');
      }

      console.log("✅ GoogleRequest created:", result);

      if (!result?.request_id) {
        console.error("❌ No request_id in response:", result);
        throw new Error("Server did not provide authorization request ID");
      }

      console.log("=== INITIATING OAUTH REDIRECT ===");
      window.location.href = buildGoogleAuthUrl({
        state: result.request_id,
        redirectUri: REDIRECT_URI,
        scope: GMAIL_SCOPES,
      });

    } catch (err) {
      console.error("❌ Partner upgrade error:", err);
      setErrorMsg(err.message || "Failed to start Partner upgrade. Please try again.");
      setLoading(false);
    }
  };

  const handleUnlinkGmail = async () => {
    setUnlinkDialogOpen(false);
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const result = await client.creatorConnect.unlinkGmail();

      if (result.success) {
        setSuccessMsg("✅ Gmail disconnected. You've been reverted to Associate status.");
        
        // Refresh status
        setTimeout(() => fetchStatus(false), 500);
      } else {
        throw new Error(result.error || 'Failed to unlink Gmail');
      }
    } catch (err) {
      console.error("Gmail disconnect error:", err);
      setErrorMsg(`Failed to disconnect Gmail: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEmailProcessing = async (newValue) => {
    const oldValue = status.process_emails;
    
    // Optimistic update
    setStatus(prev => ({ ...prev, process_emails: newValue }));
    setLoading(true);
    setErrorMsg("");

    try {
      const result = await client.creatorConnect.toggleEmailAutomation(newValue);

      if (result.success) {
        setSuccessMsg(
          newValue
            ? "✅ AI Email Agent enabled! Your AI will now process partnership emails."
            : "⏸️ AI Email Agent paused. Email processing temporarily disabled."
        );
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        throw new Error(result.error || 'Failed to toggle');
      }
    } catch (err) {
      // Revert on error
      setStatus(prev => ({ ...prev, process_emails: oldValue }));
      setErrorMsg(err.message);
      console.error("Error toggling email automation:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveForwardedAlias = async (newAlias) => {
    setLoading(true);
    setErrorMsg("");

    try {
      const result = await client.creatorConnect.updatePartnerSettings({
        forwarded_alias: newAlias
      });

      if (result.success) {
        setStatus(prev => ({ ...prev, forwarded_alias: newAlias }));
        setSuccessMsg("✅ Forwarding address updated successfully");
        setEditingAlias(false);
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        throw new Error(result.error || 'Failed to update alias');
      }
    } catch (err) {
      setErrorMsg(`Failed to update forwarding address: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ==================== RENDER ====================

  if (statusLoading) {
    return (
      <Box sx={{ maxWidth: 1000, mx: "auto", display: tabValue === index ? 'block' : 'none' }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={150} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", display: tabValue === index ? 'block' : 'none' }}>
      {/* Status Messages */}
      {errorMsg && (
        <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg("")} sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      {/* Status Overview Card */}
      <StatusCard 
        status={status.creator_status}
        isGmailConnected={isGmailConnected}
        emailProcessing={status.process_emails}
        onRefresh={() => fetchStatus(true)}
        refreshing={statusLoading}
      />

      {/* Benefits Section (only show for non-Partners) */}
      {!isPartner && <PartnerBenefits />}

      {/* Main Action Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {isPartner && isGmailConnected ? (
          // ========== PARTNER VIEW ==========
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  🎉 Partner Status Active
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your AI email agent is managing partnership opportunities
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={() => setUnlinkDialogOpen(true)}
                disabled={loading}
              >
                Disconnect & Downgrade
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Email Processing Toggle */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  AI Email Agent
                </Typography>
                <Button
                  variant={status.process_emails ? "contained" : "outlined"}
                  color={status.process_emails ? "success" : "primary"}
                  onClick={() => handleToggleEmailProcessing(!status.process_emails)}
                  disabled={loading}
                  size="small"
                >
                  {status.process_emails ? "✓ Active" : "Paused"}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {status.process_emails 
                  ? "AI is actively processing and responding to partnership emails"
                  : "Email processing is paused. Enable to let AI handle emails automatically"}
              </Typography>
            </Box>

            {/* Forwarded Alias Management */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Email Forwarding
                </Typography>
                {!editingAlias && (
                  <IconButton size="small" onClick={() => setEditingAlias(true)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {editingAlias ? (
                <ForwardedAliasEditor
                  currentAlias={status.forwarded_alias}
                  onSave={handleSaveForwardedAlias}
                  onCancel={() => setEditingAlias(false)}
                />
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Current partner address: {status.forwarded_alias || status.email || "Not set"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Partnership emails will use this email
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Info Alert */}
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2">
                <strong>What your AI is doing:</strong> Scanning inbox for partnership opportunities, 
                negotiating rates, organizing deals, and keeping you updated on progress.
              </Typography>
            </Alert>
          </Box>
        ) : (
          // ========== NON-PARTNER VIEW ==========
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Upgrade to Partner Status
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Connect your Gmail account to unlock AI-powered email automation and Partner benefits.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>What we'll access:</strong>
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Read partnership emails from brands" 
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Send negotiated responses on your behalf" 
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Organize and label partnership communications" 
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              </List>
              <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                🔒 You can revoke access anytime from your Google account settings.
              </Typography>
            </Alert>

            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleUpgradeToPartner}
              disabled={loading}
              startIcon={<UpgradeIcon />}
              sx={{ 
                py: 1.5,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5568d3 0%, #6941a5 100%)",
                }
              }}
            >
              Upgrade to Partner Status
            </Button>

            {loading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "center" }}>
                  Redirecting to Google for authorization...
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Additional Info for Partners */}
      {isPartner && isGmailConnected && (
        <Paper elevation={1} sx={{ p: 2, background: "#f5f5f5" }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Note:</strong> Your AI agent respects your preferences and will only engage with 
            legitimate partnership opportunities. You maintain full control and can review all 
            communications in your dashboard.
          </Typography>
        </Paper>
      )}

      {/* ========== DIALOGS ========== */}

      {/* Consent Dialog */}
      <ConsentDialog open={consentOpen} onClose={handleConsentClose} />

      {/* Unlink Confirmation Dialog */}
      <Dialog open={unlinkDialogOpen} onClose={() => setUnlinkDialogOpen(false)}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="warning" />
            <span>Disconnect Gmail & Downgrade?</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Disconnecting Gmail will:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <WarningIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText primary="Disable AI email automation" />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <WarningIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText primary="Revert your status to Associate" />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <WarningIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText primary="Stop automatic deal negotiation" />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You can reconnect anytime to restore Partner benefits.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setUnlinkDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleUnlinkGmail}
            disabled={loading}
          >
            Disconnect Gmail
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}